import { useEffect, useState } from "react";
import { Calendar, BookOpen, Clock, Loader2 } from "lucide-react";
import { tutorApi } from "../lib/tutorApi";
import TutorMobileLayout, { MobileSkeletonCard } from "../components/tutor/TutorMobileLayout";

/**
 * V25 Phase 5 — Tutor Calendar / Timetable (mobile-first).
 * Aggregates this tutor's upcoming demos + ongoing tuition sessions for the
 * next 7 days.
 */
export default function TutorCalendar() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                // Pull demos + ongoing tuitions in parallel
                const [{ data: demos }, { data: ong }] = await Promise.all([
                    tutorApi.get("/tutor/me/demos?status=active"),
                    tutorApi.get("/tutor/me/ongoing-tuitions"),
                ]);
                const events = [];
                for (const d of (demos.items || [])) {
                    if (d.demo_scheduled_at || d.preferred_demo_at) {
                        events.push({
                            id: `demo-${d.assignment_id}`,
                            kind: "demo",
                            title: `Demo · ${d.student_name}`,
                            subtitle: `${d.grade} · ${d.subjects}`,
                            at: d.demo_scheduled_at || d.preferred_demo_at,
                        });
                    }
                }
                for (const o of (ong.items || [])) {
                    events.push({
                        id: `ong-${o.id}`,
                        kind: "ongoing",
                        title: `Class · ${o.student_name}`,
                        subtitle: o.schedule,
                        at: null,  // recurring
                    });
                }
                events.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
                setItems(events);
            } finally { setLoading(false); }
        })();
    }, []);

    const grouped = groupByDay(items);

    return (
        <TutorMobileLayout title="Calendar" backTo="/tutor/dashboard">
            {loading ? (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <MobileSkeletonCard key={i} />)}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="tutor-calendar-empty">
                    <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <div className="font-semibold text-slate-700">Nothing scheduled</div>
                    <p className="text-xs text-slate-500 mt-1">
                        Upcoming demos &amp; tuition sessions will appear here.
                    </p>
                </div>
            ) : (
                <div className="space-y-4" data-testid="tutor-calendar-list">
                    {Object.entries(grouped).map(([day, events]) => (
                        <section key={day}>
                            <h2 className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2 px-1">{day}</h2>
                            <div className="space-y-2">
                                {events.map((e) => (
                                    <div key={e.id} data-testid={`tutor-calendar-event-${e.id}`}
                                        className="bg-white border border-slate-200 rounded-2xl p-3 flex items-start gap-3 shadow-sm">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${e.kind === "demo" ? "bg-gradient-to-br from-orange-500 to-rose-500" : "bg-gradient-to-br from-emerald-500 to-teal-600"}`}>
                                            {e.kind === "demo" ? <Calendar className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-[#0F172A] truncate">{e.title}</div>
                                            <div className="text-[11px] text-slate-500">{e.subtitle}</div>
                                            {e.at && (
                                                <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> {new Date(e.at).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </TutorMobileLayout>
    );
}

function groupByDay(events) {
    const groups = { "Today": [], "Tomorrow": [], "This Week": [], "Recurring": [] };
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const toDay = (d) => d.toISOString().slice(0, 10);
    const todayStr = toDay(today);
    const tomStr = toDay(tomorrow);
    for (const e of events) {
        if (!e.at) { groups["Recurring"].push(e); continue; }
        const day = e.at.slice(0, 10);
        if (day === todayStr) groups["Today"].push(e);
        else if (day === tomStr) groups["Tomorrow"].push(e);
        else groups["This Week"].push(e);
    }
    // Clean empty groups
    return Object.fromEntries(Object.entries(groups).filter(([, v]) => v.length > 0));
}
